import jsPDF from "jspdf";
import "jspdf-autotable";

const COLORS = {
  primary: [26, 82, 118],    // #1A5276
  accent: [40, 116, 166],    // #2874A6
  text: [33, 37, 41],        // dark
  muted: [108, 117, 125],
  light: [235, 240, 245],
  white: [255, 255, 255],
  warning: [183, 28, 28]
};

export function generatePassportPDF(data) {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const W = doc.internal.pageSize.getWidth();
  const M = 15; // margin
  let y = 0;

  // ── Helper functions ──
  function addPage() {
    doc.addPage();
    y = 15;
    // footer on every page
    addFooter();
  }

  function checkSpace(needed) {
    if (y + needed > 270) addPage();
  }

  function addFooter() {
    const pageH = doc.internal.pageSize.getHeight();
    doc.setFontSize(7);
    doc.setTextColor(...COLORS.muted);
    doc.text("Generated by Epilepsy Passport — open-source tool | epilepsy-passport.org", W / 2, pageH - 6, { align: "center" });
    doc.text(`Page ${doc.internal.getNumberOfPages()}`, W - M, pageH - 6, { align: "right" });
    doc.text(`Last updated: ${new Date(data.meta?.lastUpdated || Date.now()).toLocaleDateString()}`, M, pageH - 6);
  }

  function sectionHeader(title) {
    checkSpace(14);
    y += 4;
    doc.setFillColor(...COLORS.primary);
    doc.rect(M, y, W - 2 * M, 8, "F");
    doc.setFontSize(11);
    doc.setTextColor(...COLORS.white);
    doc.setFont("helvetica", "bold");
    doc.text(title.toUpperCase(), M + 3, y + 5.8);
    y += 12;
    doc.setTextColor(...COLORS.text);
  }

  function labelValue(label, value, indent = 0) {
    if (!value && value !== 0) return;
    checkSpace(7);
    doc.setFontSize(9);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...COLORS.muted);
    doc.text(label + ":", M + indent, y);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...COLORS.text);
    const maxW = W - 2 * M - indent - doc.getTextWidth(label + ": ");
    const lines = doc.splitTextToSize(String(value), maxW > 20 ? maxW : W - 2 * M - 5);
    if (lines.length === 1) {
      doc.text(String(value), M + indent + doc.getTextWidth(label + ": ") + 1, y);
      y += 5;
    } else {
      y += 5;
      doc.text(lines, M + indent + 2, y);
      y += lines.length * 4;
    }
  }

  function warningBox(text) {
    checkSpace(12);
    doc.setFillColor(255, 243, 224);
    doc.setDrawColor(...COLORS.warning);
    doc.roundedRect(M, y, W - 2 * M, 9, 1.5, 1.5, "FD");
    doc.setFontSize(8);
    doc.setTextColor(...COLORS.warning);
    doc.setFont("helvetica", "bold");
    doc.text("⚠ " + text, M + 3, y + 6);
    y += 12;
    doc.setTextColor(...COLORS.text);
  }

  // ══════════════════════════════════════
  // PAGE 1: COVER / HEADER
  // ══════════════════════════════════════
  doc.setFillColor(...COLORS.primary);
  doc.rect(0, 0, W, 48, "F");

  doc.setFontSize(22);
  doc.setTextColor(...COLORS.white);
  doc.setFont("helvetica", "bold");
  doc.text("EPILEPSY PASSPORT", W / 2, 18, { align: "center" });

  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.text("Comprehensive Epilepsy Medical Summary", W / 2, 26, { align: "center" });

  // Patient name
  const fullName = [data.demographics?.firstName, data.demographics?.lastName].filter(Boolean).join(" ") || "Patient Name";
  doc.setFontSize(16);
  doc.setFont("helvetica", "bold");
  doc.text(fullName, W / 2, 36, { align: "center" });

  doc.setFontSize(8);
  doc.setFont("helvetica", "normal");
  const dob = data.demographics?.dateOfBirth || "";
  if (dob) doc.text("Date of Birth: " + dob, W / 2, 43, { align: "center" });

  y = 56;

  // ── 1. DEMOGRAPHICS ──
  sectionHeader("Demographics & Emergency Contacts");
  const d = data.demographics || {};
  labelValue("Name", fullName);
  labelValue("Date of Birth", d.dateOfBirth);
  labelValue("Sex", d.sex);
  labelValue("Primary Neurologist", d.primaryNeurologist);
  labelValue("Neurologist Phone", d.neurologistPhone);
  labelValue("Epilepsy Center", d.epilepsyCenter);

  if (d.emergencyContact1Name) {
    y += 2;
    doc.setFontSize(9);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...COLORS.accent);
    doc.text("Emergency Contact 1", M, y);
    y += 5;
    doc.setTextColor(...COLORS.text);
    labelValue("Name", d.emergencyContact1Name, 3);
    labelValue("Phone", d.emergencyContact1Phone, 3);
    labelValue("Relationship", d.emergencyContact1Relation, 3);
  }
  if (d.emergencyContact2Name) {
    doc.setFontSize(9);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...COLORS.accent);
    doc.text("Emergency Contact 2", M, y);
    y += 5;
    doc.setTextColor(...COLORS.text);
    labelValue("Name", d.emergencyContact2Name, 3);
    labelValue("Phone", d.emergencyContact2Phone, 3);
    labelValue("Relationship", d.emergencyContact2Relation, 3);
  }

  // ── 2. DIAGNOSIS ──
  sectionHeader("Epilepsy Diagnosis Summary");
  const dx = data.diagnosis || {};
  labelValue("Epilepsy Type", dx.epilepsyType);
  labelValue("Syndrome", dx.epilepsySyndrome);
  labelValue("Etiology", dx.etiology);
  labelValue("Age of Onset", dx.ageOfOnset);
  labelValue("Date of Diagnosis", dx.dateOfDiagnosis);
  if (dx.diagnosisNotes) labelValue("Notes", dx.diagnosisNotes);

  // ── 3. SEIZURE TYPES ──
  if (data.seizureTypes?.length > 0) {
    sectionHeader("Seizure Types & Semiology");
    data.seizureTypes.forEach((sz, i) => {
      checkSpace(25);
      doc.setFontSize(9);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(...COLORS.accent);
      doc.text(`Seizure Type ${i + 1}: ${sz.classification || ""}`, M, y);
      y += 5;
      doc.setTextColor(...COLORS.text);
      labelValue("Typical Duration", sz.duration, 3);
      labelValue("Frequency", sz.frequency, 3);
      labelValue("Warning Signs / Aura", sz.aura, 3);
      if (sz.description) labelValue("Patient Description", sz.description, 3);
      y += 2;
    });
  }

  // ── 4. CURRENT MEDICATIONS ──
  if (data.currentMedications?.length > 0) {
    sectionHeader("Current Medications");
    const medRows = data.currentMedications.map(m => [
      m.name || "",
      m.dose || "",
      m.frequency || "",
      m.level || "",
      m.startDate || "",
      m.effectiveness || ""
    ]);
    doc.autoTable({
      startY: y,
      head: [["Medication", "Dose", "Frequency", "Blood Level", "Start Date", "Effectiveness"]],
      body: medRows,
      margin: { left: M, right: M },
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: COLORS.primary, textColor: COLORS.white, fontStyle: "bold" },
      alternateRowStyles: { fillColor: COLORS.light },
      theme: "grid"
    });
    y = doc.lastAutoTable.finalY + 4;
  }

  // Valproate warning for reproductive-age patients
  const hasValproate = data.currentMedications?.some(m =>
    m.name && /valpro|depakote|depakene|epilim/i.test(m.name)
  );
  const isFemale = /female|f/i.test(data.demographics?.sex || "");
  if (hasValproate && isFemale) {
    warningBox("Patient is on valproate — review pregnancy prevention program and folic acid supplementation.");
  }

  // ── 5. MEDICATION HISTORY ──
  if (data.medicationHistory?.length > 0) {
    sectionHeader("Medication History (All Prior ASMs)");
    const histRows = data.medicationHistory.map(m => [
      m.name || "",
      m.peakDose || "",
      m.duration || "",
      m.reason || "",
      m.notes || ""
    ]);
    doc.autoTable({
      startY: y,
      head: [["Medication", "Peak Dose", "Duration", "Reason Stopped", "Notes"]],
      body: histRows,
      margin: { left: M, right: M },
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: COLORS.primary, textColor: COLORS.white, fontStyle: "bold" },
      alternateRowStyles: { fillColor: COLORS.light },
      theme: "grid",
      columnStyles: { 3: { cellWidth: 40 } }
    });
    y = doc.lastAutoTable.finalY + 4;
  }

  // ── 6. DIAGNOSTICS ──
  const diag = data.diagnostics || {};
  const hasDiagnostics = diag.eegs?.length || diag.mris?.length || diag.geneticTests?.length || diag.otherTests?.length;
  if (hasDiagnostics) {
    sectionHeader("Diagnostic Workup Summary");

    if (diag.eegs?.length) {
      checkSpace(10);
      doc.setFontSize(9);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(...COLORS.accent);
      doc.text("EEG Studies", M, y);
      y += 5;
      doc.setTextColor(...COLORS.text);
      diag.eegs.forEach(e => {
        labelValue("Date", e.date, 3);
        labelValue("Findings", e.findings, 3);
        if (e.notes) labelValue("Notes", e.notes, 3);
        y += 2;
      });
    }

    if (diag.mris?.length) {
      checkSpace(10);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(...COLORS.accent);
      doc.text("MRI Studies", M, y);
      y += 5;
      doc.setTextColor(...COLORS.text);
      diag.mris.forEach(m => {
        labelValue("Date", m.date, 3);
        labelValue("Findings", m.findings, 3);
        if (m.notes) labelValue("Notes", m.notes, 3);
        y += 2;
      });
    }

    if (diag.geneticTests?.length) {
      checkSpace(10);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(...COLORS.accent);
      doc.text("Genetic Testing", M, y);
      y += 5;
      doc.setTextColor(...COLORS.text);
      diag.geneticTests.forEach(g => {
        labelValue("Gene", g.gene, 3);
        labelValue("Variant", g.variant, 3);
        labelValue("Classification", g.classification, 3);
        y += 2;
      });
    }

    if (diag.otherTests?.length) {
      checkSpace(10);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(...COLORS.accent);
      doc.text("Other Tests", M, y);
      y += 5;
      doc.setTextColor(...COLORS.text);
      diag.otherTests.forEach(t => {
        labelValue("Test", t.testName, 3);
        labelValue("Date", t.date, 3);
        labelValue("Findings", t.findings, 3);
        y += 2;
      });
    }
  }

  // ── 7. SURGERY ──
  const surg = data.surgery || {};
  if (surg.hasSurgery || surg.neuromodulation !== "None") {
    sectionHeader("Surgical History & Neuromodulation");
    if (surg.surgeries?.length) {
      surg.surgeries.forEach(s => {
        labelValue("Procedure", s.type, 3);
        labelValue("Date", s.date, 3);
        labelValue("Outcome", s.outcome, 3);
        y += 2;
      });
    }
    labelValue("Neuromodulation", surg.neuromodulation);
    if (surg.neuromodulationDetails) labelValue("Device Details", surg.neuromodulationDetails);
    if (surg.presurgicalStatus) labelValue("Presurgical Evaluation Status", surg.presurgicalStatus);
  }

  // ── 8. COMORBIDITIES ──
  const co = data.comorbidities || {};
  if (co.psychiatric || co.cognitive || co.neurological || co.otherMedical) {
    sectionHeader("Comorbidities");
    labelValue("Psychiatric", co.psychiatric);
    labelValue("Cognitive / Developmental", co.cognitive);
    labelValue("Other Neurological", co.neurological);
    labelValue("Other Medical", co.otherMedical);
    if (co.notes) labelValue("Notes", co.notes);
  }

  // ── 9. CURRENT STATUS ──
  sectionHeader("Current Status & Goals");
  const cs = data.currentStatus || {};
  labelValue("Current Seizure Frequency", cs.currentFrequency);
  labelValue("Last Seizure Date", cs.lastSeizureDate);
  labelValue("Seizure-Free Duration", cs.seizureFreedomDuration);
  labelValue("Driving Eligibility", cs.drivingEligibility);
  labelValue("Employment / School", cs.employmentStatus);
  if (cs.qualityOfLifePriorities) labelValue("Quality of Life Priorities", cs.qualityOfLifePriorities);

  // ── 10. SPECIAL CONSIDERATIONS ──
  const sp = data.specialConsiderations || {};
  const hasSpecial = sp.sudepDiscussed || sp.rescueMedication || sp.seizureActionPlan || sp.pregnancyConsiderations || sp.dietaryTherapy;
  if (hasSpecial) {
    sectionHeader("Special Considerations");
    labelValue("SUDEP Risk Discussed", sp.sudepDiscussed);
    labelValue("Rescue Medication", sp.rescueMedication);
    if (sp.rescueMedicationDetails) labelValue("Rescue Details", sp.rescueMedicationDetails);
    if (sp.seizureActionPlan) labelValue("Seizure Action Plan", sp.seizureActionPlan);
    if (sp.pregnancyConsiderations) labelValue("Pregnancy Considerations", sp.pregnancyConsiderations);
    if (sp.folicAcid) labelValue("Folic Acid", sp.folicAcid);
    if (sp.dietaryTherapy) labelValue("Dietary Therapy", sp.dietaryTherapy);
    if (sp.dietaryDetails) labelValue("Diet Details", sp.dietaryDetails);
    if (sp.additionalNotes) labelValue("Additional Notes", sp.additionalNotes);
  }

  // Add footer to last page
  addFooter();

  // ── SAVE ──
  const fileName = `Epilepsy_Passport_${fullName.replace(/\s+/g, "_")}_${new Date().toISOString().slice(0, 10)}.pdf`;
  doc.save(fileName);
}
